import { FC } from 'react';
import { NavLink } from 'react-router-dom';
import { Drawer, List, ListItem } from '@mui/material';
import { RegionEnum } from '~/api/types';
import { ENV } from '~/config';
import { useRegion } from '~/hooks';
import { CatalogIcon, DashboardIcon, SageMakerIcon, SettingsIcon, StorageIcon } from '~/icons';
import { useAppSelector } from '~/redux';
import { selectCurrentUserIsAdmin } from '~/redux/slices/currentUserSlice';
import NavigationItemButton from './NavigationItemButton';
import { listNavigationItemStyles, navigationDrawerStyles } from './styles';

interface LeftNavigationBarProps {
  isDrawerOpen: boolean;
}

const LeftNavigationBar: FC<LeftNavigationBarProps> = ({ isDrawerOpen }) => {
  const isAdmin = useAppSelector(selectCurrentUserIsAdmin);
  const region = useRegion();

  const navigation = [
    {
      icon: <DashboardIcon />,
      label: 'Project Dashboard',
      path: '/projects',
      testId: 'dashboard-nav-link',
    },
    {
      icon: <CatalogIcon />,
      label: 'Data Catalog',
      path: '/data-catalog',
      testId: 'data-catalog-nav-link',
    },
    {
      icon: <StorageIcon />,
      label: 'My Storage',
      path: '/storage',
      testId: 'storage-nav-link',
    },
    ...(region === RegionEnum.US
      ? [
          {
            icon: <SageMakerIcon />,
            label: 'SageMaker',
            path: ENV.VITE_SAGE_MAKER_URL,
            testId: 'sagemaker-nav-link',
            external: true,
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            icon: <SettingsIcon />,
            label: 'Admin Settings',
            path: '/settings',
            testId: 'admin-settings-nav-link',
          },
        ]
      : []),
  ];

  return (
    <Drawer variant="permanent" open={isDrawerOpen} sx={navigationDrawerStyles(isDrawerOpen)}>
      <List>
        {navigation.map(({ icon, label, path, testId, external }) => (
          <ListItem key={path} disablePadding sx={listNavigationItemStyles}>
            {external ? (
              <a href={path} target="_blank" rel="noopener noreferrer" data-testid={testId}>
                <NavigationItemButton icon={icon} label={label} isDrawerOpen={isDrawerOpen} />
              </a>
            ) : (
              <NavLink to={path} className={({ isActive }) => (isActive ? 'active' : '')} data-testid={testId}>
                <NavigationItemButton icon={icon} label={label} isDrawerOpen={isDrawerOpen} />
              </NavLink>
            )}
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export default LeftNavigationBar;
